import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset token. Please open the link from your email again.');
      return;
    }
    if (password.length < 8) return toast.error('Password must be at least 8 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/auth'), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. Try requesting a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <h1 className="text-3xl font-bold text-[#001f5c] mb-2">Set a new password</h1>
        <p className="text-sm text-slate-600 mb-8">Choose a strong password. You'll be signed out of every device after resetting so you can start fresh.</p>

        {done ? (
          <div className="p-5 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="reset-done-block">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-emerald-900">Password updated</p>
                <p className="text-sm text-emerald-800 mt-1">Redirecting you to the sign-in page…</p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
            <div className="space-y-1.5">
              <Label htmlFor="rp-new" className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500">New Password</Label>
              <div className="relative">
                <Input
                  id="rp-new"
                  type={show ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 border-slate-300 focus:border-[#00aaff] focus:ring-[#00aaff] pr-10"
                  placeholder="Min 8 chars: upper, lower, digit, special"
                  data-testid="rp-new-input"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  data-testid="rp-toggle-visibility"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-confirm" className="text-[11px] font-semibold tracking-[0.14em] uppercase text-slate-500">Confirm New Password</Label>
              <Input
                id="rp-confirm"
                type={show ? 'text' : 'password'}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-11 border-slate-300 focus:border-[#00aaff] focus:ring-[#00aaff]"
                data-testid="rp-confirm-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#001f5c] hover:bg-[#001845] text-white font-semibold" data-testid="rp-submit">
              {loading ? 'Resetting...' : 'Reset password'}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
